// How far a live stream's playback has fallen behind, and when to reconnect.
//
// RCS-33. The player has no stall recovery and no live resync: once the audio
// element falls behind it stays behind, and every subsequent interruption adds
// more. David saw the now-playing title running ~105s ahead of the audio on
// 1 Sep 2026; reloading the page reset it to ~10-12s, after which it drifted
// out again.
//
// THE MECHANISM IS DISCRETE STALLS, NOT A RATE. The ticket originally blamed
// faster-than-realtime delivery (1.37x, then 1.06x). Both figures were the same
// measurement error: the CDN sends a ~31s burst of buffered audio on connect
// and then delivers at exactly 1.0000x, so a fixed burst amortised over a short
// window looks like a rate. Measured over 40 minutes: 2250.7s of audio in
// 2251.2s of wall clock. Nothing accrues on its own.
//
// So the only way to reach 105s is stall events that never recover, and that is
// precisely what "no resync path" produces. This module measures that directly
// rather than modelling a rate that does not exist: while the player intends to
// play, playback position must advance one second per second of wall clock.
// Any shortfall IS stall time, and it is permanent until something reconnects.
//
// Deliberately pure and free of timers, DOM and React so the arithmetic can be
// tested without a stream. The player supplies the samples.

/** Seconds of accumulated shortfall before a reconnect is worthwhile. */
export const DEFAULT_DRIFT_THRESHOLD_SECONDS = 12;

/**
 * A backwards jump larger than this means the position was reset by something
 * other than playback -- a reconnect, a new source, a seek -- so the baseline
 * has to be re-established rather than counted as drift.
 */
const POSITION_RESET_TOLERANCE_SECONDS = 1;

/**
 * A gap between samples longer than this is not trustworthy as stall evidence.
 * A backgrounded tab, a slept laptop or a paused debugger all stop the sampler
 * without the listener losing any audio, and counting that as drift would
 * reconnect a stream that was playing perfectly.
 */
const MAX_TRUSTED_SAMPLE_GAP_SECONDS = 5;

export function initialDriftState() {
  return {
    driftSeconds: 0,
    lastWallMs: null,
    lastPositionSeconds: null,
  };
}

/**
 * Fold one observation into the drift state.
 *
 * @param {object} state     previous state, from initialDriftState()
 * @param {object} sample
 * @param {boolean} sample.playing   the player intends to be playing live
 * @param {number}  sample.wallMs    Date.now() at the observation
 * @param {number}  sample.positionSeconds  the audio element's currentTime
 * @returns {object} the next state
 */
export function observe(state, { playing, wallMs, positionSeconds }) {
  // Not playing: hold the accumulated drift but drop the baseline, so a pause
  // is never counted as falling behind. A listener who pauses for an hour has
  // not drifted; they have paused.
  if (!playing) {
    return { ...state, lastWallMs: null, lastPositionSeconds: null };
  }

  // First sample of a playing run establishes the baseline and nothing else.
  if (state.lastWallMs === null || state.lastPositionSeconds === null) {
    return { ...state, lastWallMs: wallMs, lastPositionSeconds: positionSeconds };
  }

  const wallElapsed = (wallMs - state.lastWallMs) / 1000;
  const played = positionSeconds - state.lastPositionSeconds;

  // Position went backwards: the source restarted under us. Re-baseline and
  // clear the drift, because whatever we were measuring no longer exists.
  if (played < -POSITION_RESET_TOLERANCE_SECONDS) {
    return { driftSeconds: 0, lastWallMs: wallMs, lastPositionSeconds: positionSeconds };
  }

  // Time went backwards, or no time passed. Nothing to learn.
  if (wallElapsed <= 0) {
    return { ...state, lastWallMs: wallMs, lastPositionSeconds: positionSeconds };
  }

  // The sampler itself was starved. Re-baseline rather than blame the stream.
  if (wallElapsed > MAX_TRUSTED_SAMPLE_GAP_SECONDS) {
    return { ...state, lastWallMs: wallMs, lastPositionSeconds: positionSeconds };
  }

  // The measurement. Playback that advanced less than wall clock was stalled
  // for the difference. Playback cannot advance FASTER than realtime on a live
  // stream, so a negative shortfall is noise and is floored at zero rather
  // than allowed to pay off real drift already accumulated.
  const shortfall = Math.max(0, wallElapsed - played);

  return {
    driftSeconds: state.driftSeconds + shortfall,
    lastWallMs: wallMs,
    lastPositionSeconds: positionSeconds,
  };
}

/**
 * Has enough shortfall accumulated to be worth a reconnect?
 *
 * Reconnecting is not free -- it drops the listener's audio for a moment and
 * costs a fresh ~31s burst -- so the threshold has to sit above the ordinary
 * hiccup and well below the ~105s that prompted the ticket.
 */
export function shouldResync(state, threshold = DEFAULT_DRIFT_THRESHOLD_SECONDS) {
  return state.driftSeconds >= threshold;
}

/** Called after a reconnect, so the same drift is not acted on twice. */
export function afterResync() {
  return initialDriftState();
}
